import React, { useState, useEffect } from 'react';
import { Mail, Send, CheckCircle, AlertCircle, Users, Loader2, History, RefreshCw, RotateCcw, ChevronUp, ChevronDown } from 'lucide-react';
import apiClient from '../api/client';

export default function EmailTestPage() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [resending, setResending] = useState({});
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    loadUsers();
    loadLog();
  }, []);

  async function loadUsers() {
    try {
      const res = await apiClient.get('/notifications/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadLog() {
    try {
      const res = await apiClient.get('/notifications/log', { params: { pageSize: 50 } });
      setLog(res.data.notifications || []);
    } catch (err) {
      console.error('Failed to load log:', err);
    }
  }

  async function handleResend(notificationId) {
    setResending(prev => ({ ...prev, [notificationId]: true }));
    try {
      await apiClient.post(`/notifications/resend/${notificationId}`);
      await loadLog();
    } catch (err) {
      console.error('Resend failed:', err);
    } finally {
      setResending(prev => ({ ...prev, [notificationId]: false }));
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!selectedUserId || !subject.trim() || !message.trim()) return;

    setSending(true);
    setResult(null);

    try {
      const res = await apiClient.post('/notifications/test', {
        recipientUserId: selectedUserId,
        subject: subject.trim(),
        message: message.trim(),
      });
      setResult({ success: true, message: `Email sent to ${res.data.recipientEmail}` });
      setSubject('');
      setMessage('');
      setSelectedUserId('');
      loadLog();
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || 'Failed to send email' });
    } finally {
      setSending(false);
    }
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const sortedLog = [...log].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'CreatedAt') {
      return dir * (new Date(a.CreatedAt) - new Date(b.CreatedAt));
    }
    const aVal = (a[sortField] || '').toString();
    const bVal = (b[sortField] || '').toString();
    return dir * aVal.localeCompare(bVal);
  });

  const selectedUser = users.find(u => u.UserID === selectedUserId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mail className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Test</h1>
            <p className="text-sm text-gray-500">Send a test email to verify notification delivery</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose form */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Compose Test Email</h2>

            <form onSubmit={handleSend} className="space-y-4">
              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users className="h-4 w-4 inline mr-1" />
                  Recipient
                </label>
                {loading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading users...
                  </div>
                ) : (
                  <select
                    value={selectedUserId}
                    onChange={e => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a user...</option>
                    {users.map(u => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.DisplayName || u.Username} — {u.Email}
                      </option>
                    ))}
                  </select>
                )}
                {selectedUser && (
                  <p className="mt-1 text-xs text-gray-500">
                    Will send to: <span className="font-medium">{selectedUser.Email}</span>
                  </p>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Test email subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Type your test message here..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                  required
                />
              </div>

              {/* Result banner */}
              {result && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  result.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {result.success ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {result.message}
                </div>
              )}

              {/* Send */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={sending || !selectedUserId || !subject.trim() || !message.trim()}
                  className="btn-primary flex items-center gap-2"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Quick info panel */}
        <div className="space-y-4">
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Email Configuration</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Sender Address</dt>
                <dd className="font-mono text-xs text-gray-900 mt-0.5">DoNotReply@notifications.spocodoc.com</dd>
              </div>
              <div>
                <dt className="text-gray-500">Service Provider</dt>
                <dd className="text-gray-900 mt-0.5">Azure Communication Services</dd>
              </div>
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd className="mt-0.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                    Active
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Notification Types</h3>
            <ul className="space-y-1.5 text-xs text-gray-600">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400"></span>Invoice submitted for approval</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400"></span>Invoice advanced to next approver</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-green-400"></span>Invoice fully approved</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-red-400"></span>Invoice rejected</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400"></span>Expense submitted for approval</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-green-400"></span>Expense approved</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-red-400"></span>Expense rejected</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Notification Log — inline below compose */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Notification Log</h2>
            <span className="text-xs text-gray-400">({log.length})</span>
          </div>
          <button onClick={loadLog} className="btn-secondary flex items-center gap-1.5 text-sm py-1.5 px-3">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto overflow-auto max-h-[50vh] border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('AzureMessageID')}><div className="flex items-center gap-1">Message ID<SortIcon field="AzureMessageID" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('CreatedAt')}><div className="flex items-center gap-1">Date<SortIcon field="CreatedAt" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('RecipientName')}><div className="flex items-center gap-1">Recipient<SortIcon field="RecipientName" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Subject')}><div className="flex items-center gap-1">Subject<SortIcon field="Subject" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('NotificationType')}><div className="flex items-center gap-1">Type<SortIcon field="NotificationType" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}><div className="flex items-center gap-1">Status<SortIcon field="Status" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedLog.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No notifications sent yet</td></tr>
              ) : sortedLog.map(n => (
                <tr key={n.NotificationID} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-400 max-w-[120px] truncate" title={n.AzureMessageID || '—'}>
                    {n.AzureMessageID ? n.AzureMessageID.substring(0, 12) + '...' : '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                    {new Date(n.CreatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-2">{n.RecipientName || n.RecipientEmail}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate">{n.Subject}</td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">{n.NotificationType}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      n.Status === 'Sent' ? 'bg-green-100 text-green-700' :
                      n.Status === 'Failed' ? 'bg-red-100 text-red-700' :
                      n.Status === 'DryRun' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{n.Status}</span>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleResend(n.NotificationID)}
                      disabled={resending[n.NotificationID]}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                      title="Resend this notification"
                    >
                      {resending[n.NotificationID]
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RotateCcw className="h-3.5 w-3.5" />
                      }
                      Resend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
